/**
 * Following a case's Monitor stream (PRD-0075 R-B6): Server-Sent Events over `fetch`, so the bearer
 * token is sent, and kept open while the case is open in a visible tab — reconnecting with backoff and
 * fresh headers after a drop, a deploy or an expired token. A hidden tab closes it and opens it again
 * when shown: over HTTP/1.1 (the dev server) a browser holds six connections per host, and one stream
 * per background tab used them up until every page hung. A 403 or 404 (no longer a member, no such
 * case) ends it for good. The server says `ready` on every (re)connect and comments a keep-alive
 * every 15 s, so a proxy never cuts an idle stream. Without a live channel on the server (no Valkey)
 * it says `unavailable` with how long to wait, and the tab relies on polling until then.
 */
import { EventStreamContentType, fetchEventSource } from '@microsoft/fetch-event-source'

/** One event of the stream: `ready`, `alerts_raised` or `alert_triaged`, with what the server sent. */
export interface MonitorStreamEvent {
  type: string
  [field: string]: unknown
}

export interface MonitorStreamOptions {
  url: string
  signal: AbortSignal
  /** The request headers, asked for again before each connect (a token may have been refreshed). */
  headers: () => Promise<Record<string, string>>
  onEvent: (event: MonitorStreamEvent) => void
  /** How long to wait before reconnect number `attempt` (1, 2, …), in ms. */
  backoff?: (attempt: number) => number
  /** The SSE transport; a test passes its own. */
  open?: typeof fetchEventSource
}

/** The stream may not be followed: the reader is no member, or the case is gone. */
class StreamRefused extends Error {}

const MAX_BACKOFF_MS = 30_000
/** The wait after `unavailable` when the server names none. */
export const UNAVAILABLE_PAUSE_MS = 300_000

export const defaultBackoff = (attempt: number) =>
  Math.min(MAX_BACKOFF_MS, 1000 * 2 ** (attempt - 1))

function decoded(name: string, data: string): MonitorStreamEvent | null {
  try {
    const parsed: unknown = JSON.parse(data)
    if (typeof parsed !== 'object' || parsed === null) return null
    const fields = parsed as Record<string, unknown>
    return { ...fields, type: typeof fields.type === 'string' ? fields.type : name }
  } catch {
    return null
  }
}

function pause(ms: number, signal: AbortSignal): Promise<void> {
  return new Promise(resolve => {
    const timer = setTimeout(resolve, ms)
    signal.addEventListener('abort', () => {
      clearTimeout(timer)
      resolve()
    })
  })
}

/** What one connection ended with: how long to wait before the next one, if the server said. */
interface Connection {
  /** `ready` was heard: the channel works, so the backoff starts over. */
  heard: () => void
  /** `unavailable` was heard: wait this long (ms) before trying again. */
  unavailable: (waitMs: number) => void
}

async function connectOnce(options: MonitorStreamOptions, connection: Connection): Promise<void> {
  const open = options.open ?? fetchEventSource
  await open(options.url, {
    signal: options.signal,
    headers: await options.headers(),
    // `ready` on reopening makes the tab read its bell and inbox again, so nothing is missed.
    openWhenHidden: false,
    onopen: async response => {
      if (response.ok && response.headers.get('content-type')?.includes(EventStreamContentType))
        return
      if (response.status === 403 || response.status === 404) throw new StreamRefused()
      throw new Error(`monitor stream answered ${response.status}`)
    },
    onmessage: message => {
      const event = decoded(message.event, message.data)
      if (!event) return
      if (event.type === 'ready') connection.heard()
      if (event.type === 'unavailable') {
        connection.unavailable(
          typeof event.retry_ms === 'number' ? event.retry_ms : UNAVAILABLE_PAUSE_MS
        )
      }
      options.onEvent(event)
    },
    onclose: () => {
      throw new Error('monitor stream closed')
    },
    onerror: error => {
      throw error // no retry inside the transport: the loop below reconnects with fresh headers
    },
  })
}

/** Follows the stream until `signal` aborts or the server refuses it. */
export async function followMonitorStream(options: MonitorStreamOptions): Promise<void> {
  const backoff = options.backoff ?? defaultBackoff
  let attempt = 0
  while (!options.signal.aborted) {
    let waitMs: number | null = null
    try {
      await connectOnce(options, {
        heard: () => {
          attempt = 0
        },
        unavailable: ms => {
          waitMs = ms
        },
      })
    } catch (error) {
      if (error instanceof StreamRefused) return
    }
    if (options.signal.aborted) return
    attempt += 1
    await pause(waitMs ?? backoff(attempt), options.signal)
  }
}
